import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import type { CityStateSuggestion } from './city-state-search';
import { searchCityStateSuggestions } from './city-state-search';

type CityStateAutocompleteProps = {
  error?: string;
  label: string;
  onBlur?: () => void;
  onChangeText: (value: string | null) => void;
  placeholder?: string;
  value: string | null;
};

export function CityStateAutocomplete({
  error,
  label,
  onBlur,
  onChangeText,
  placeholder = 'Start typing a city',
  value,
}: CityStateAutocompleteProps) {
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<CityStateSuggestion[]>([]);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const query = value ?? '';
  const showSuggestions = focused && suggestions.length > 0;
  const showSearchStatus =
    focused && query.trim().length >= 2 && (loading || searchError);

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (!focused || trimmedQuery.length < 2) {
      setLoading(false);
      setSearchError(null);
      setSuggestions([]);
      return undefined;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => {
      setLoading(true);
      setSearchError(null);

      searchCityStateSuggestions({
        query: trimmedQuery,
        signal: controller.signal,
      })
        .then((results) => {
          setSuggestions(results);
          setSearchError(null);
        })
        .catch((caughtError: unknown) => {
          if (controller.signal.aborted) {
            return;
          }

          setSuggestions([]);
          setSearchError(
            caughtError instanceof Error
              ? caughtError.message
              : 'City search is temporarily unavailable.',
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        });
    }, 220);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [focused, query]);

  function clearBlurTimeout() {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        autoCapitalize="words"
        onBlur={() => {
          blurTimeoutRef.current = setTimeout(() => {
            setFocused(false);
            onBlur?.();
          }, 120);
        }}
        onChangeText={(nextValue) => {
          onChangeText(nextValue || null);
        }}
        onFocus={() => {
          clearBlurTimeout();
          setFocused(true);
        }}
        placeholder={placeholder}
        placeholderTextColor={colors.mutedText}
        selectionColor={colors.primary}
        style={[styles.input, error ? styles.inputError : null]}
        value={value ?? ''}
      />
      {showSuggestions ? (
        <View style={styles.suggestionPanel}>
          {suggestions.map((suggestion) => (
            <Pressable
              key={`${suggestion.city}-${suggestion.stateCode}`}
              accessibilityRole="button"
              onPress={() => {
                clearBlurTimeout();
                onChangeText(suggestion.label);
                setFocused(false);
                onBlur?.();
              }}
              style={({ pressed }) => [
                styles.suggestion,
                pressed ? styles.suggestionPressed : null,
              ]}
            >
              <Text style={styles.suggestionCity}>{suggestion.city}</Text>
              <Text style={styles.suggestionState}>
                {suggestion.secondaryLabel}
              </Text>
            </Pressable>
          ))}
        </View>
      ) : null}
      {showSearchStatus ? (
        <View style={styles.searchStatus}>
          {loading ? <ActivityIndicator color={colors.primary} /> : null}
          <Text style={styles.searchStatusText}>
            {loading ? 'Searching cities...' : searchError}
          </Text>
        </View>
      ) : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: spacing.xs,
  },
  error: {
    color: colors.danger,
    fontSize: 13,
  },
  input: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    color: colors.text,
    fontSize: 16,
    minHeight: 48,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  inputError: {
    borderColor: colors.danger,
  },
  label: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '700',
  },
  suggestion: {
    gap: 2,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  suggestionCity: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  suggestionPanel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  suggestionPressed: {
    backgroundColor: colors.surfaceTint,
  },
  suggestionState: {
    color: colors.mutedText,
    fontSize: 12,
  },
  searchStatus: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  searchStatusText: {
    color: colors.mutedText,
    fontSize: 12,
  },
});
